import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useUrlParams } from '../hooks/useUrlState';
import { api, BonusTierConfig } from '../lib/api';
import { fmtCurrency, fmtMonth, fmtPct } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  ActionButton, MetricCard, SectionLabel, Badge, Card, CardHeader, Table, Th, Tr, Td,
  DrillPanel, Spinner, EmptyState, TierProgress,
  PeriodPicker, resolvePeriod, type PeriodPreset,
} from '../components/ui';
import { DrilldownModal } from '../components/DrilldownModal';
import { TeamDrilldownModal } from '../components/TeamDrilldownModal';
import { ChartCard, ShippedTrendChart } from '../components/charts';

const DEFAULT_PERIOD_PARAMS = { preset: 'last12', from: '', to: '' };

function resolveTier(sv: number, tiers: BonusTierConfig[]): BonusTierConfig | null {
  for (let i = tiers.length - 1; i >= 0; i--) {
    if (sv >= Number(tiers[i].threshold_amount)) return tiers[i];
  }
  return null;
}

function tierBadge(tier: number | null) {
  if (!tier) return <Badge variant="gray">Below tier</Badge>;
  return <Badge variant="green">Tier {tier}</Badge>;
}

/** Arrow + percent. Colour-codes up/down so execs scanning the table can
 *  read direction without parsing numbers. `compact` drops the arrow when
 *  the delta is already next to a comparator value (peer avg). */
function TrendDelta({ delta, compact = false }: { delta: number | null; compact?: boolean }) {
  if (delta == null) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  const pct = Math.round(delta * 1000) / 10; // one decimal
  const up = delta > 0;
  const flat = Math.abs(delta) < 0.005;
  const color = flat ? 'var(--color-text-muted)' : up ? 'var(--color-green-text)' : 'var(--color-red-text)';
  const arrow = flat ? '•' : up ? '▲' : '▼';
  return (
    <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
      {!compact && <span style={{ marginRight: 2 }}>{arrow}</span>}
      {flat ? '0%' : `${up ? '+' : ''}${pct.toFixed(1)}%`}
    </span>
  );
}

type DrillTab = 'shipped' | 'wip';

function DrillTabs({
  tab, onChange, shippedCount, wipCount,
}: {
  tab: DrillTab;
  onChange: (next: DrillTab) => void;
  shippedCount: number;
  wipCount: number;
}) {
  const tabs: Array<{ key: DrillTab; label: string; count: number; accent: string }> = [
    { key: 'shipped', label: 'Shipped',     count: shippedCount, accent: 'var(--color-green)' },
    { key: 'wip',     label: 'WIP planned', count: wipCount,     accent: 'var(--color-amber)' },
  ];
  return (
    <div style={{ display: 'flex', gap: 4, padding: '8px 12px 0', borderBottom: '0.5px solid var(--color-border)' }}>
      {tabs.map((t) => {
        const active = t.key === tab;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              padding: '6px 10px', fontSize: 12, fontWeight: active ? 600 : 500,
              color: active ? t.accent : 'var(--color-text-muted)',
              borderBottom: active ? `2px solid ${t.accent}` : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label} <span style={{ fontWeight: 400, opacity: 0.7 }}>· {t.count}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function ScorecardPage() {
  const { user } = useAuth();
  const [openPeriod, setOpenPeriod] = useState<string | null>(null);
  const [drillTab, setDrillTab] = useState<DrillTab>('shipped');
  const [modal, setModal] = useState<{
    month: string;
    metricLabel: string;
    metricValue: string;
    kind: 'shipped' | 'wip';
    defaultTab: 'monthly' | 'weekly';
  } | null>(null);
  const [teamModal, setTeamModal] = useState<{
    month: string;
    metricLabel: string;
    metricValue: string;
    kind: 'shipped' | 'wip';
    defaultTab: 'monthly' | 'weekly';
  } | null>(null);
  const [periodParams, setPeriodParams] = useUrlParams(DEFAULT_PERIOD_PARAMS);
  const period = resolvePeriod(
    periodParams.preset as PeriodPreset,
    periodParams.from || undefined,
    periodParams.to || undefined,
  );
  const months = period.monthsCount;

  const { data, isLoading, error } = useQuery(
    () => api.scorecard.get(user!.jira_user_id, months),
    [user?.jira_user_id, months],
  );

  // Both Shipped and WIP lists for the open period — fetched eagerly so the
  // tab counts are accurate before the user clicks the WIP tab, and so the
  // tab switch is instant. Backend swaps `month` from attribution_month
  // (actual ship month) to due_date (planned ship month) when status=WIP.
  const { data: drillShippedData, isLoading: drillShippedLoading } = useQuery(
    () => openPeriod
      ? api.scorecard.deliverables(user!.jira_user_id, { month: openPeriod, status: 'Shipped' })
      : Promise.resolve(null),
    [user?.jira_user_id, openPeriod],
  );
  const { data: drillWipData, isLoading: drillWipLoading } = useQuery(
    () => openPeriod
      ? api.scorecard.deliverables(user!.jira_user_id, { month: openPeriod, status: 'WIP' })
      : Promise.resolve(null),
    [user?.jira_user_id, openPeriod],
  );

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={`Error: ${error}`} />;
  if (!data) return null;

  const { periods, tier_config } = data;
  const isRollup = !!data.is_rollup;
  const currentPeriod = periods[0];
  const tier = !isRollup ? resolveTier(currentPeriod?.shipped_value ?? 0, tier_config) : null;
  const nextTier = !isRollup
    ? (tier ? tier_config.find((t) => t.tier === tier.tier + 1) : tier_config[0])
    : undefined;
  const gap = nextTier ? nextTier.threshold_amount - (currentPeriod?.shipped_value ?? 0) : 0;

  function toggleDrill(month: string, locked: boolean) {
    if (locked) return;
    // On rollup scorecards, open the team drill-down modal instead of the
    // personal inline DrillPanel. The row click is the exec's entry point
    // into the per-member breakdown for that month.
    if (isRollup) {
      const period = periods.find((p) => p.attribution_month === month);
      setTeamModal({
        month,
        metricLabel: 'Team shipped',
        metricValue: fmtCurrency(period?.shipped_value ?? 0),
        kind: 'shipped',
        defaultTab: 'monthly',
      });
      return;
    }
    // Re-open default to the Shipped tab regardless of what was last viewed,
    // so users land on the headline number first.
    setDrillTab('shipped');
    setOpenPeriod(openPeriod === month ? null : month);
  }

  return (
    <>
      {/* Current period summary */}
      {currentPeriod && (
        <div>
          <SectionLabel>
            {fmtMonth(currentPeriod.attribution_month)} — current period
            {isRollup && (
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                · team rollup of {data.team_rollup?.member_count ?? 0} reports
              </span>
            )}
          </SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
            <MetricCard
              label={isRollup ? 'Shipped value (team)' : 'Shipped value'}
              value={fmtCurrency(currentPeriod.shipped_value)}
              accent="green"
              onClick={isRollup
                ? () => setTeamModal({
                    month: currentPeriod.attribution_month,
                    metricLabel: 'Shipped value',
                    metricValue: fmtCurrency(currentPeriod.shipped_value),
                    kind: 'shipped',
                    defaultTab: 'monthly',
                  })
                : () => setModal({
                    month: currentPeriod.attribution_month,
                    metricLabel: 'Shipped value',
                    metricValue: fmtCurrency(currentPeriod.shipped_value),
                    kind: 'shipped',
                    defaultTab: 'monthly',
                  })}
              sub={isRollup
                ? undefined
                : (
                  <TierProgress
                    value={currentPeriod.shipped_value}
                    tiers={tier_config.map((t) => ({ threshold_amount: Number(t.threshold_amount), tier: t.tier }))}
                  />
                )}
            />
            <MetricCard
              label={isRollup ? 'WIP (cumulative, team)' : 'WIP value'}
              value={fmtCurrency(isRollup ? (data.team_rollup?.total_wip_value ?? 0) : currentPeriod.wip_value)}
              accent="amber"
              onClick={isRollup
                ? () => setTeamModal({
                    month: currentPeriod.attribution_month,
                    metricLabel: 'WIP — hours logged by week',
                    metricValue: fmtCurrency(data.team_rollup?.total_wip_value ?? 0),
                    kind: 'wip',
                    defaultTab: 'weekly',
                  })
                : () => setModal({
                    month: currentPeriod.attribution_month,
                    metricLabel: 'WIP — hours logged by week',
                    metricValue: fmtCurrency(currentPeriod.wip_value),
                    kind: 'wip',
                    defaultTab: 'weekly',
                  })}
              sub={isRollup
                ? 'Across all unshipped deliverables'
                : 'Hours logged on unshipped deliverables'}
            />
            {!isRollup && (
              <>
                <MetricCard
                  label="Bonus tier"
                  value={tier ? <Badge variant="green">Tier {tier.tier}</Badge> : <Badge variant="gray">No tier yet</Badge>}
                  sub={gap > 0 ? `${fmtCurrency(gap)} to ${nextTier ? `Tier ${nextTier.tier}` : 'next tier'}` : 'Top tier reached'}
                />
                <MetricCard
                  label="Gross bonus (est.)"
                  value={currentPeriod.gross_bonus ? fmtCurrency(currentPeriod.gross_bonus) : '—'}
                  accent={currentPeriod.gross_bonus ? 'green' : undefined}
                  sub={`Paid next month if tier maintained`}
                />
              </>
            )}
            {isRollup && (
              <>
                <MetricCard
                  label="Direct + indirect reports"
                  value={data.team_rollup?.member_count ?? 0}
                  sub="Active in the subtree"
                />
                <MetricCard
                  label="View"
                  value={<span style={{ fontSize: 13 }}>Team rollup</span>}
                  sub="Director / exec-level view"
                />
              </>
            )}
          </div>
        </div>
      )}

      {/* Shipped + WIP trend — last N months at a glance, with tier lines. */}
      {periods.length > 0 && (
        <ChartCard
          title={isRollup ? 'Team shipped trend' : 'Shipped + WIP trend'}
          sub={`${months} months`}
          height={220}
        >
          <ShippedTrendChart
            periods={periods}
            tiers={isRollup ? [] : tier_config.map((t) => ({
              tier: t.tier,
              threshold_amount: Number(t.threshold_amount),
            }))}
          />
        </ChartCard>
      )}

      {/* Period history table */}
      <div>
        <SectionLabel>Period history</SectionLabel>
        <Card>
          <CardHeader>
            <span style={{ fontSize: 12, fontWeight: 500 }}>
              {isRollup ? 'Team scorecard (rollup)' : 'Bonus scorecard'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
              {isRollup ? 'Per-month rollup across your reports' : 'Click any unlocked row to see deliverable breakdown'}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <PeriodPicker
                value={period}
                onChange={(v) => setPeriodParams({
                  preset: v.preset,
                  from: v.preset === 'custom' ? v.from : '',
                  to:   v.preset === 'custom' ? v.to   : '',
                })}
              />
              <ActionButton
                onClick={() => {
                  const rows: Array<Array<unknown>> = [[
                    'Period', 'Shipped value', 'WIP value', 'Canceled value',
                    'Tier', 'Multiplier', 'Gross bonus', 'Clawback', 'Net bonus', 'Locked',
                  ]];
                  for (const p of periods) {
                    rows.push([
                      p.attribution_month,
                      p.shipped_value.toFixed(2),
                      p.wip_value.toFixed(2),
                      p.canceled_value.toFixed(2),
                      p.bonus_tier ?? '',
                      p.bonus_multiplier ?? '',
                      p.gross_bonus?.toFixed(2) ?? '',
                      p.clawback_applied?.toFixed(2) ?? '',
                      p.net_bonus?.toFixed(2) ?? '',
                      p.is_locked ? 'yes' : 'no',
                    ]);
                  }
                  downloadCsv(`scorecard-${user?.jira_user_id ?? 'me'}.csv`, rows);
                }}
                variant="default"
                disabled={periods.length === 0}
              >
                Export CSV
              </ActionButton>
            </span>
          </CardHeader>
          <Table>
            <thead>
              <tr>
                <Th>Period</Th>
                <Th>Shipped value</Th>
                <Th>MoM</Th>
                {!isRollup && <Th>Peer avg</Th>}
                <Th>WIP (this month due)</Th>
                <Th>Canceled (forfeit)</Th>
                {!isRollup && <Th>Tier</Th>}
                {!isRollup && <Th>Multiplier</Th>}
                {!isRollup && <Th>Gross bonus</Th>}
                {!isRollup && <Th>Clawback</Th>}
                {!isRollup && <Th>Net bonus</Th>}
                {!isRollup && <Th> </Th>}
              </tr>
            </thead>
            <tbody>
              {periods.map((p, i) => {
                // periods come in "most recent first", so the PRIOR month is the
                // next index. Use it directly (no re-sort) to avoid a subtle bug.
                const prior = periods[i + 1];
                const delta = prior && prior.shipped_value > 0
                  ? (p.shipped_value - prior.shipped_value) / prior.shipped_value
                  : null;
                const peerAvg = p.role_avg_shipped_value ?? null;
                const vsPeers = peerAvg != null && peerAvg > 0
                  ? (p.shipped_value - peerAvg) / peerAvg
                  : null;
                const _t = !isRollup ? resolveTier(p.shipped_value, tier_config) : null; void _t;
                const isCurrent = p.attribution_month === currentPeriod?.attribution_month;
                const isOpen = openPeriod === p.attribution_month;
                return (
                  <Tr
                    key={p.attribution_month}
                    onClick={() => toggleDrill(p.attribution_month, p.is_locked)}
                    selected={isOpen}
                  >
                    <Td bold>
                      {fmtMonth(p.attribution_month)}{' '}
                      {isCurrent && <Badge variant="blue">current</Badge>}
                    </Td>
                    <Td>
                      {fmtCurrency(p.shipped_value)}
                      {isCurrent && <span style={{ color: 'var(--color-text-muted)', fontSize: 11, marginLeft: 4 }}>(in progress)</span>}
                    </Td>
                    <Td muted style={{ fontSize: 11 }}><TrendDelta delta={delta} /></Td>
                    {!isRollup && (
                      <Td muted style={{ fontSize: 11 }}>
                        {peerAvg != null ? (
                          <>
                            {fmtCurrency(peerAvg)}
                            {vsPeers != null && <span style={{ marginLeft: 4 }}><TrendDelta delta={vsPeers} compact /></span>}
                          </>
                        ) : '—'}
                      </Td>
                    )}
                    <Td muted>{fmtCurrency(p.wip_value)}</Td>
                    <Td muted style={{ color: p.canceled_value > 0 ? 'var(--color-red-text)' : undefined }}>
                      {p.canceled_value > 0 ? fmtCurrency(p.canceled_value) : '—'}
                    </Td>
                    {!isRollup && <Td>{tierBadge(p.bonus_tier)}</Td>}
                    {!isRollup && <Td>{p.bonus_multiplier ? fmtPct(p.bonus_multiplier) : '—'}</Td>}
                    {!isRollup && (
                      <Td style={{ color: p.gross_bonus ? 'var(--color-green)' : undefined }}>
                        {p.gross_bonus ? fmtCurrency(p.gross_bonus) : '—'}
                      </Td>
                    )}
                    {!isRollup && (
                      <Td muted style={{ color: p.clawback_applied ? 'var(--color-red-text)' : 'var(--color-text-muted)', fontSize: 11 }}>
                        {p.clawback_applied ? `−${fmtCurrency(p.clawback_applied)}` : '—'}
                      </Td>
                    )}
                    {!isRollup && (
                      <Td bold style={{ color: (p.net_bonus ?? 0) > 0 ? 'var(--color-green)' : undefined }}>
                        {p.net_bonus != null ? fmtCurrency(p.net_bonus) : '—'}
                      </Td>
                    )}
                    {!isRollup && (
                      <Td>
                        {p.is_locked
                          ? <Badge variant="locked">Locked</Badge>
                          : <span style={{ color: 'var(--color-blue)', fontSize: 11 }}>Detail {isOpen ? '↑' : '↓'}</span>}
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </Card>
      </div>

      {/* Drill-down modal (from clicking a metric card) */}
      {modal && user && (
        <DrilldownModal
          userId={user.jira_user_id}
          userLabel={user.display_name}
          month={modal.month}
          metricLabel={modal.metricLabel}
          metricValue={modal.metricValue}
          kind={modal.kind}
          defaultTab={modal.defaultTab}
          onClose={() => setModal(null)}
        />
      )}

      {/* Team rollup drill-down (director / exec view) */}
      {teamModal && user && data.team_rollup && (
        <TeamDrilldownModal
          rootUserId={user.jira_user_id}
          rootUserLabel={user.display_name}
          month={teamModal.month}
          metricLabel={teamModal.metricLabel}
          metricValue={teamModal.metricValue}
          members={data.team_rollup.members}
          kind={teamModal.kind}
          defaultTab={teamModal.defaultTab}
          onClose={() => setTeamModal(null)}
        />
      )}

      {/* Inline drill panel (from clicking a period row). Shipped uses the
          actual completed-date month; WIP uses the planned due-date month
          (mirrors bonusService.calcIcShipped/WipValue). */}
      {openPeriod && (() => {
        const p = periods.find((x) => x.attribution_month === openPeriod);
        const t = p ? resolveTier(p.shipped_value, tier_config) : null;
        const shippedRows = drillShippedData?.deliverables ?? [];
        const wipRows = drillWipData?.deliverables ?? [];
        const shippedLoading = drillShippedLoading;
        const wipLoading = drillWipLoading;
        const activeRows = drillTab === 'shipped' ? shippedRows : wipRows;
        const activeLoading = drillTab === 'shipped' ? shippedLoading : wipLoading;
        const valueColor = drillTab === 'shipped' ? 'var(--color-green)' : 'var(--color-amber)';
        const valueHeader =
          drillTab === 'shipped'
            ? (user?.role === 'IC' ? 'My contribution' : 'Shipped value')
            : (user?.role === 'IC' ? 'My WIP contribution' : 'WIP value');
        const hoursHeader = drillTab === 'shipped'
          ? (user?.role === 'IC' ? 'My hours' : 'Total hours')
          : (user?.role === 'IC' ? 'My hours (so far)' : 'Total hours (so far)');
        const emptyMsg = drillTab === 'shipped'
          ? 'No shipped deliverables found for this period.'
          : 'No WIP deliverables planned for this period.';
        return (
          <DrillPanel
            title={`${fmtMonth(openPeriod)} — deliverable breakdown`}
            meta={p && (
              <span>
                {fmtCurrency(p.shipped_value)} shipped &nbsp;·&nbsp; {fmtCurrency(p.wip_value)} WIP
                &nbsp;·&nbsp; {t ? `Tier ${t.tier} · ${fmtPct(Number(t.multiplier))} → ` : 'Below tier — '}
                {t ? <strong>{fmtCurrency(p.gross_bonus ?? 0)}</strong> : 'no bonus'}
              </span>
            )}
            onClose={() => setOpenPeriod(null)}
          >
            <DrillTabs
              tab={drillTab}
              onChange={setDrillTab}
              shippedCount={shippedRows.length}
              wipCount={wipRows.length}
            />
            {activeLoading ? (
              <Spinner />
            ) : activeRows.length === 0 ? (
              <EmptyState message={emptyMsg} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Deliverable</Th>
                    <Th>Engagement</Th>
                    <Th>{hoursHeader}</Th>
                    <Th>Rate</Th>
                    <Th>{valueHeader}</Th>
                  </tr>
                </thead>
                <tbody>
                  {activeRows.map((d) => (
                    <Tr key={d.jira_issue_id}>
                      <Td bold>{d.summary}</Td>
                      <Td muted style={{ fontSize: 11 }}>{d.tempo_account_name}</Td>
                      <Td>{user?.role === 'IC' ? `${d.my_billable_hours}h` : `${d.total_billable_hours}h`}</Td>
                      <Td>${d.billable_rate}/h</Td>
                      <Td bold style={{ color: valueColor }}>{fmtCurrency(d.shipped_value)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </DrillPanel>
        );
      })()}

      {/* Team rollup — player/coach directors get this underneath their
          personal scorecard. For pure directors/execs the primary periods
          already ARE the rollup, so this block is skipped. */}
      {!isRollup && data.team_rollup && (
        <div>
          <SectionLabel>
            Team rollup — {data.team_rollup.member_count} report{data.team_rollup.member_count === 1 ? '' : 's'}
          </SectionLabel>
          <Card>
            <CardHeader>
              <span style={{ fontSize: 12, fontWeight: 500 }}>Your team's performance</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                Cumulative WIP: <strong>{fmtCurrency(data.team_rollup.total_wip_value)}</strong>
                {data.team_rollup.total_canceled_value != null && data.team_rollup.total_canceled_value > 0 && (
                  <span style={{ marginLeft: 8 }}>
                    · Canceled (forfeit): <strong style={{ color: 'var(--color-red-text)' }}>{fmtCurrency(data.team_rollup.total_canceled_value)}</strong>
                  </span>
                )}
              </span>
            </CardHeader>
            <Table>
              <thead>
                <tr>
                  <Th>Period</Th>
                  <Th right>Team shipped</Th>
                  <Th right>Team WIP (due this month)</Th>
                  <Th right>Canceled (forfeit)</Th>
                </tr>
              </thead>
              <tbody>
                {data.team_rollup.periods.map((p) => (
                  <Tr key={p.attribution_month}>
                    <Td bold>{fmtMonth(p.attribution_month)}</Td>
                    <Td right style={{ color: p.shipped_value > 0 ? 'var(--color-green)' : undefined }}>
                      {fmtCurrency(p.shipped_value)}
                    </Td>
                    <Td right muted>{fmtCurrency(p.wip_value)}</Td>
                    <Td right muted style={{ color: p.canceled_value > 0 ? 'var(--color-red-text)' : undefined }}>
                      {p.canceled_value > 0 ? fmtCurrency(p.canceled_value) : '—'}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </div>
      )}

      {/* Tier reference — only for bonus-eligible roles */}
      {!isRollup && (
        <div>
          <SectionLabel>Tier thresholds — {user?.role}</SectionLabel>
          <div style={{ display: 'flex', gap: 10 }}>
            {tier_config.map((t) => (
              <div key={t.tier} style={{ background: 'var(--color-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Tier {t.tier}</div>
                <div style={{ fontSize: 15, fontWeight: 500, marginTop: 2 }}>{fmtCurrency(Number(t.threshold_amount))}+</div>
                <div style={{ fontSize: 12, color: 'var(--color-green-text)', marginTop: 2 }}>{fmtPct(Number(t.multiplier))} bonus</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
